import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useId } from 'react-use-id-hook';
import './styles.scss';
import {
  ExpandMode,
  MaterialIcon,
  MaterialIconName,
  Pill,
  PillTypes,
  Well,
  WellType,
} from 'client/shared/components/base';
import { BenchmarkIndicator } from 'client/shared/components/benchmark-indicator';
import {
  MMMM_YEAR_FORMAT,
  YEAR_FORMAT,
  TrackVariable,
  FipsArea,
  VariableDisplayType,
  NationalDataFips,
  wrap,
} from 'core';
import { DifferenceIndicator } from 'client/shared/components/difference-indicator';
import { CollapseBtn } from 'client/shared/components/base/collapse-btn';
import {
  BenchmarkFilter,
  DateLevel,
} from 'client/shared/graphql-client/graphql-operations.g';
import {
  analyticsBenchmarkToClient,
  formatIndicatorValue,
  getFilteredBenchmarkFooter,
  getFilteredBenchmarkValue,
} from 'client/shared/core/performance-data';
import { DownloadImageFooter } from 'client/shared/components/download-image-footer';
import { useGenerateImage } from 'client/shared/hooks';
import { Tooltip } from '../tooltip';
import { EmbedLogoFooter } from '../embed-logo-footer';
import { EmbedDataPointContext } from '../visualization-picker';
import { demographicSegmentLabel } from '../domain-card-indicator';
import { TrackLineChart } from '../domain-indicators-group/domain-indicators-group-line-chart';
import { DomainIndicatorsFipsAreaRow } from '../domain-indicators-group/domain-indicators-group-wrapper';
import { GqlGoal } from 'client/admin/hooks/use-track-goals';
import { useAdminPermissions } from 'client/admin/hooks/use-admin-permissions';
import { PermissionType, PredictiveModelType } from '@polco-us/types';
import { ManageGoalModal } from 'client/admin/track/overview/components/manage-goal-modal';
import { AIQuickAction } from '../ai-quick-actions';
import {
  enabledOptionState,
  hiddenOptionState,
  SaveableDataPoint,
  TrackCardOptionsMenu,
  TrackCardOptionType,
} from '../track-card-options-menu';
import { COLORS_LIBERTY_HEX } from 'client/shared/core/colors';
import { IconButton } from '@mui/material';

export interface Props extends SaveableDataPoint {
  readonly benchmarkFilter: BenchmarkFilter;
  readonly className?: string;
  readonly variable: TrackVariable;
  readonly currentFips: Pick<FipsArea, 'id'>;
  readonly expanded?: boolean;
  readonly toggleExpanded?: (variableId: string) => void;
  readonly embedContext?: EmbedDataPointContext;
  readonly showFooter?: boolean;
  readonly displayType?: VariableDisplayType;
  readonly comparisonGroupId?: string;
  readonly goal?: GqlGoal;
  readonly planId?: string;
  readonly publishingEntityId: string | null;
  readonly hideAiQuickAction?: boolean;
  readonly hidePlaceName?: boolean;
  readonly showPredictive?: boolean;
  readonly handlePredictionToggle?: () => void;
}

const baseClass = 'pn-domain-community-statistic';

const communityStatisticsCopy = {
  differenceIndicator:
    "This number represents the change in this indicator's value since the previous measurement.",
  dataUnavailable: 'Data unavailable',
  chartWell: (description: string | null, fipsName?: string) => {
    return (
      <>
        This chart illustrates the change over time in
        <span className="font-weight-bold"> {description}</span>
        {!!fipsName && (
          <>
            {' for '}
            <span className="font-weight-bold">{fipsName}</span>
          </>
        )}
        .
      </>
    );
  },
  noChartText: (description: string | null, fipsName?: string) => {
    return (
      <>
        <span className="font-weight-bold"> {description}</span>
        {!!fipsName && (
          <>
            {' for '}
            <span className="font-weight-bold">{fipsName}</span>
          </>
        )}
        .
      </>
    );
  },
  indicatorMetaData: (args: {
    readonly source: string;
    readonly recordedAt: Date | null;
    readonly dateLevel: DateLevel | null;
  }) => {
    const dateFormat =
      args.dateLevel === DateLevel.YEAR ? YEAR_FORMAT : MMMM_YEAR_FORMAT;
    return (
      <div
        className={`${baseClass}-indicator-metadata font-size-xs mt-3 display-flex justify-content-end`}
      >
        <div>
          Data from <span className="font-weight-bold">{args.source}</span>
          {args.recordedAt && (
            <>
              {' in '}
              <span className="font-weight-bold">
                {moment.utc(args.recordedAt).format(dateFormat)}
              </span>
            </>
          )}
        </div>
      </div>
    );
  },
};

export const CommunityStatisticsCard: React.FC<Props> = (p) => {
  const communityStatisticsId = useId();
  const ref = React.useRef<HTMLDivElement>(null);
  const generateImage = useGenerateImage(ref, {
    imageName: `${p.variable.label} indicator`,
  });
  const [goalModalOpen, setGoalModalOpen] = React.useState(false);
  const variable = p.variable;
  const demographicSegment = variable.demographicSegment;
  const { doesCurrentAdminHavePermissions: havePermissions } = useAdminPermissions();
  const canManageGoals = havePermissions([PermissionType.MANAGE_TRACK_GOALS]);

  const sortedValues = _.reverse(
    _.sortBy(
      variable.areasData.find((ad) => ad.fipsArea.id === p.currentFips.id)
        ?.performanceData,
      ({ recordedAt }) => recordedAt
    )
  );

  const recentValue =
    sortedValues.length && sortedValues[0].value !== null ? sortedValues[0] : null;

  const sortedArPredictiveData = variable.areasData.find(
    (ad) => ad.fipsArea.id === p.currentFips.id
  )?.arPredictiveData;
  const sortedGpalPredictiveData = variable.areasData.find(
    (ad) => ad.fipsArea.id === p.currentFips.id
  )?.gpalPredictiveData;

  const lineData: readonly DomainIndicatorsFipsAreaRow[] = React.useMemo(
    () =>
      _.compact([
        recentValue
          ? {
              id: variable.id,
              rank: 0,
              dataAreaFips: recentValue.fipsArea.id,
              name: recentValue.fipsArea.name,
              color: COLORS_LIBERTY_HEX,
              recentDatum: recentValue,
              trackAreaData: {
                fipsArea: recentValue.fipsArea,
                performanceData: sortedValues,
                arPredictiveData: sortedArPredictiveData ?? [],
                gpalPredictiveData: sortedGpalPredictiveData ?? [],
              },
              variableName: variable.name,
              variableValueType: variable.valueType,
              variableDateLevel: variable.dateLevel,
              variableDirection: variable.direction,
            }
          : null,
      ]),
    [
      variable,
      recentValue,
      sortedValues,
      sortedArPredictiveData,
      sortedGpalPredictiveData,
    ]
  );

  const lineVisibility = React.useMemo(
    () => (recentValue ? { [recentValue.fipsArea.id]: true } : null),
    [recentValue]
  );

  const [hoverLine, setHoverLine] = React.useState<{
    readonly fips: string;
    readonly modelType: PredictiveModelType;
  } | null>(null);

  if (!p.variable.areasData.length) {
    return (
      <div className={`${baseClass} ${p.className || ''} p-3`}>
        <div className="font-weight-bold font-size-lg text-gray-40">
          {communityStatisticsCopy.dataUnavailable}
        </div>
      </div>
    );
  }

  const isMultiplePointsData = sortedValues.length > 1;

  const benchmarkValue = recentValue
    ? getFilteredBenchmarkValue(recentValue, p.benchmarkFilter)
    : null;

  const footer =
    p.currentFips.id !== NationalDataFips && variable.direction
      ? getFilteredBenchmarkFooter(p.benchmarkFilter)
      : undefined;
  return (
    <div
      className={`${baseClass} ${p.className || ''} p-3 ${
        p.embedContext === EmbedDataPointContext.VISUALIZATION ? 'border-0' : ''
      }`}
      id={communityStatisticsId}
      ref={ref}
    >
      <div className="d-flex justify-content-between">
        <div
          className={`${baseClass}-title-container d-flex flex-wrap justify-content-between align-items-start`}
        >
          <div className="d-flex align-items-start flex-column justify-content-start">
            <div className={`font-weight-bold font-size-lg mr-4`}>
              {variable.label}
            </div>
            {p.displayType === VariableDisplayType.OLDER_ADULTS && (
              <div
                className={`${baseClass}-demographic-segment d-flex d-row flex-wrap justify-content-start`}
              >
                <Pill
                  className="segment"
                  key={demographicSegment}
                  type={PillTypes.FACET}
                >
                  {demographicSegmentLabel(demographicSegment)}
                </Pill>
              </div>
            )}
            {benchmarkValue && p.variable.direction && (
              <BenchmarkIndicator
                benchmarkValue={analyticsBenchmarkToClient(benchmarkValue)}
                className={`${baseClass}-benchmark-indicator my-1`}
                expandMode={ExpandMode.ALWAYS_EXPANDED}
              />
            )}
          </div>
        </div>
        <div className="d-flex">
          <TrackCardOptionsMenu
            generateImage={{
              type: TrackCardOptionType.SAVE_IMAGE,
              state: enabledOptionState(generateImage),
            }}
            saveVisualization={p.saveVisualization}
            setGoalModalOpen={{
              type: TrackCardOptionType.OPEN_GOAL_MODAL,
              state: wrap(() => {
                if (!canManageGoals || !p.planId) {
                  return hiddenOptionState();
                }
                return enabledOptionState(() => setGoalModalOpen(true));
              }),
            }}
          />
          {!!p.toggleExpanded && (
            <CollapseBtn
              action={() => p.toggleExpanded?.(variable.id)}
              ariaControls={communityStatisticsId}
              expanded={!!p.expanded}
            />
          )}
        </div>
      </div>
      {p.showFooter && (
        <div className="font-size-sm py-2 mb-3 no-show-in-image">{footer}</div>
      )}
      {!p.hideAiQuickAction && (
        <AIQuickAction className="my-1" promptKey={variable.name} />
      )}
      <div
        className={`${baseClass}-data d-flex flex-row flex-wrap justify-content-between align-items-center`}
      >
        {recentValue ? (
          <>
            <div className={`d-flex flex-row align-items-center`}>
              <div
                className={`${baseClass}-indicator-value font-weight-bold mr-2 text-liberty`}
              >
                {formatIndicatorValue(recentValue.value, variable.valueType)}
              </div>
              <div className="font-size-sm text-gray-40">{variable.suffix}</div>
              {recentValue.previousValue && (
                <DifferenceIndicator
                  className="ml-2 cursor-pointer"
                  difference={recentValue.value - recentValue.previousValue?.value}
                  isNeutralPalette
                  tooltip={communityStatisticsCopy.differenceIndicator}
                  tooltipId={`difference-indicator-information-${variable.name}`}
                  valueType={variable.valueType}
                  variableDateLevel={variable.dateLevel}
                />
              )}
              {recentValue.fipsArea.id !== p.currentFips.id && (
                <Tooltip
                  content={`Data from ${recentValue.fipsArea.name}`}
                  id={`demographics-${p.currentFips}-${variable.name}`}
                >
                  <MaterialIcon
                    ariaLabel={`Data from ${recentValue.fipsArea.name}`}
                    className="font-size-lg ml-2"
                    icon={MaterialIconName.INFO_OUTLINE}
                  />
                </Tooltip>
              )}
            </div>
          </>
        ) : (
          <div
            className={`${baseClass}-data-unavailable font-weight-bold text-gray-40`}
          >
            {communityStatisticsCopy.dataUnavailable}
          </div>
        )}
      </div>
      {!!p.expanded && (
        <div className="mt-2 d-flex flex-column w-100">
          <PredictiveDataToggle
            handlePredictionToggle={p.handlePredictionToggle}
            hasPredictiveData={
              !!sortedArPredictiveData?.length || !!sortedGpalPredictiveData?.length
            }
            showPredictiveData={p.showPredictive}
          />
          {(!!p.goal ||
            (p.showPredictive &&
              (!!sortedArPredictiveData?.length ||
                !!sortedGpalPredictiveData?.length)) ||
            isMultiplePointsData) && (
            <div>
              <TrackLineChart
                data={lineData}
                fipsAreaVisibility={lineVisibility ?? {}}
                goalLine={p.goal?.value}
                hidePlaceName={p.hidePlaceName}
                hoverLine={hoverLine}
                predictiveDataEnabled={p.showPredictive}
                setHoverLine={setHoverLine}
                variable={variable}
              />
            </div>
          )}
          <Well
            className="mb-0"
            noTopMargin
            type={WellType.TEXT_BLOCK_WHITE_WITH_BORDER}
          >
            {isMultiplePointsData
              ? communityStatisticsCopy.chartWell(
                  variable.description,
                  recentValue?.fipsArea.name
                )
              : communityStatisticsCopy.noChartText(
                  variable.description,
                  recentValue?.fipsArea.name
                )}
          </Well>
        </div>
      )}
      {variable.source !== null &&
        communityStatisticsCopy.indicatorMetaData({
          source: variable.source,
          recordedAt: recentValue?.recordedAt ?? null,
          dateLevel: variable.dateLevel,
        })}
      <DownloadImageFooter footer={footer} />
      {p.embedContext === EmbedDataPointContext.VISUALIZATION && <EmbedLogoFooter />}
      {p.planId && p.publishingEntityId && (
        <ManageGoalModal
          currentGoalData={p.goal}
          events={{
            onGoalSaved: () => {
              if (!p.expanded) {
                p.toggleExpanded?.(variable.id);
              }
            },
            cancel: () => setGoalModalOpen(false),
          }}
          isOpen={goalModalOpen}
          planId={p.planId}
          publishingEntityId={p.publishingEntityId}
          suffix={variable.suffix}
          valueType={variable.valueType}
          variableId={variable.id}
          variableLabel={variable.label}
        />
      )}
    </div>
  );
};
CommunityStatisticsCard.displayName = 'CommunityStatisticsCard';

export const PredictiveDataToggle: React.FC<{
  readonly hasPredictiveData: boolean;
  readonly handlePredictionToggle?: () => void;
  readonly showPredictiveData?: boolean;
}> = (p) => {
  return p.hasPredictiveData && !!p.handlePredictionToggle ? (
    <div className="d-flex align-items-center font-weight-bold text-gray-60 align-self-end gap-2 mb-2 no-show-in-image">
      Predictive Data
      <IconButton
        className="p-0"
        onClick={() => {
          p.handlePredictionToggle?.();
        }}
        size="large"
      >
        <MaterialIcon
          className={`${p.showPredictiveData ? 'text-polco-green' : ''}`}
          icon={
            p.showPredictiveData
              ? MaterialIconName.TOGGLE_ON
              : MaterialIconName.TOGGLE_OFF
          }
          iconType="material-icons-outlined"
          style={{ fontSize: '32px' }}
        />
      </IconButton>
    </div>
  ) : null;
};
